/**
 * Login Page Component
 * 
 * Beautiful login interface matching KeyboardHero's design aesthetic.
 * Handles Google OAuth authentication with proper error handling and loading states.
 */

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface LoginPageProps {
  onSuccess?: () => void;
  onClose?: () => void;
  className?: string;
}

const LoginPage: React.FC<LoginPageProps> = ({ onSuccess, onClose, className = '' }) => {
  const { signIn, loading, error } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  /**
   * Handle Google sign in
   */
  const handleGoogleSignIn = async () => {
    if (isSigningIn || loading) return;

    setIsSigningIn(true);
    setLocalError(null);

    try {
      console.log('🔐 Initiating Google sign-in from LoginPage...');

      const result = await signIn();

      if (result.success) {
        console.log('✅ Sign-in successful');
        onSuccess?.();
      } else {
        console.error('🚫 Sign-in failed:', result.error);
        setLocalError(result.error || 'Sign-in failed. Please try again.');
      }
    } catch (error) {
      console.error('🚫 Unexpected sign-in error:', error);
      setLocalError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  };

  /**
   * Clear error messages
   */
  const clearError = () => {
    setLocalError(null);
  };

  const displayError = localError || error?.message;
  const isLoading = loading || isSigningIn;

  return (
    <div className={`login-page ${className}`}>
      {/* Background gradient */}
      <div className="login-background"></div>

      {/* Main content */}
      <div className="login-container">

        {/* Header */}
        <div className="login-header">
          {onClose && (
            <button className="login-close-btn" onClick={onClose}>
              &times;
            </button>
          )}
          <div className="logo">
            <img
              src="/assets/logo_KBH.png"
              alt="KeyboardHero Logo"
              className="logo-image"
            />
          </div>
          <p className="tagline">Master the art of typing with scientific precision</p>
        </div>

        {/* Login card */}
        <div className="login-card">
          <div className="card-header">
            <h2>Welcome Back</h2>
            <p>Sign in to continue your typing journey</p>
          </div>

          {/* Error display */}
          {displayError && (
            <div className="error-message">
              <div className="error-icon">⚠️</div>
              <div className="error-content">
                <p>{displayError}</p>
                <button onClick={clearError} className="error-close">×</button>
              </div>
            </div>
          )}

          {/* Google sign in button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="google-signin-btn"
          >
            {isLoading ? (
              <div className="loading-content">
                <div className="spinner"></div>
                <span>Signing in...</span>
              </div>
            ) : (
              <div className="signin-content">
                <div className="google-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24">
                    <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                </div>
                <span>Continue with Google</span>
              </div>
            )}
          </button>

          {/* Additional info */}
          <div className="login-info">
            <p className="privacy-note">
              By signing in, you agree to our terms of service and privacy policy.
              Your typing data will be securely stored to track your progress.
            </p>

            <div className="features-preview">
              <h3>Keyboard Hero is:</h3>
              <ul>
                <li>🎯 The Game with the free access for all Masters of Typing</li>
                <li>📊 Innovative EGHI-system - highly effective exclusive fast typing method</li>
                <li>🏆 Untimate EGHI-training platform with 20-days traingng course </li>
                <li>🔒 Detailed performance analyticsc and progress tracking</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="login-footer">
          <p>New to typing training? Start your journey with scientifically-designed exercises.</p>
        </div>
      </div>

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          position: relative;
          overflow: hidden;
        }

        .login-page.login-modal-content {
          min-height: auto;
          width: 100%;
          border-radius: 24px;
        }

        .login-page.login-modal-content .login-background {
          border-radius: 24px;
        }

        .login-close-btn {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.5);
          font-size: 28px;
          cursor: pointer;
          transition: color 0.2s;
          z-index: 100;
          padding: 8px;
          line-height: 1;
        }

        .login-close-btn:hover {
          color: #ffffff;
        }

        .login-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 20%, #312e81 40%, #1e293b 60%, #0f172a 100%);
          opacity: 1;
        }

        .login-background::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 70% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%);
        }

        .login-container {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 440px;
          display: flex;
          flex-direction: column;
          gap: 32px;
        }

        .login-header {
          text-align: center;
          color: #ffffff;
        }

        .logo {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        /* 🎯 LOGO IMAGE STYLING - ADJUST SIZE HERE */
        .logo-image {
          width: 200px;                    /* 👈 Change this to adjust logo width */
          height: auto;                    /* Maintains aspect ratio */
          max-width: 100%;                 /* Responsive on mobile */
          object-fit: contain;             /* Preserves logo proportions */
          filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
        }

        .tagline {
          margin: 0;
          font-size: 1rem;
          color: #9ca3af;
          font-weight: 400;
        }

        .login-card {
          background: rgba(17, 24, 39, 0.8);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(75, 85, 99, 0.3);
          border-radius: 16px;
          padding: 32px;
          box-shadow: 
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04),
            0 0 0 1px rgba(255, 255, 255, 0.05) inset;
        }

        .card-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .card-header h2 {
          margin: 0 0 8px 0;
          font-size: 1.875rem;
          font-weight: 700;
          color: #ffffff;
        }

        .card-header p {
          margin: 0;
          color: #9ca3af;
          font-size: 0.875rem;
        }

        .error-message {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          margin-bottom: 24px;
          color: #fca5a5;
        }

        .error-icon {
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .error-content {
          flex: 1;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .error-content p {
          margin: 0;
          font-size: 0.875rem;
          line-height: 1.4;
        }

        .error-close {
          background: none;
          border: none;
          color: #fca5a5;
          font-size: 1.25rem;
          cursor: pointer;
          padding: 0;
          margin-left: 12px;
          flex-shrink: 0;
        }

        .error-close:hover {
          color: #ffffff;
        }

        .google-signin-btn {
          width: 100%;
          padding: 16px 24px;
          background: #ffffff;
          color: #374151;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease-out;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          margin-bottom: 24px;
        }

        .google-signin-btn:hover:not(:disabled) {
          background: #f9fafb;
          transform: translateY(-1px);
          box-shadow: 0 8px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .google-signin-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .google-signin-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .loading-content, .signin-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #e5e7eb;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .google-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .login-info {
          border-top: 1px solid rgba(75, 85, 99, 0.3);
          padding-top: 24px;
        }

        .privacy-note {
          margin: 0 0 24px 0;
          font-size: 0.75rem;
          color: #6b7280;
          line-height: 1.5;
          text-align: center;
        }

        .features-preview {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          padding: 20px;
        }

        .features-preview h3 {
          margin: 0 0 12px 0;
          font-size: 0.875rem;
          font-weight: 600;
          color: #ffffff;
        }

        .features-preview ul {
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .features-preview li {
          font-size: 0.75rem;
          color: #9ca3af;
          margin-bottom: 8px;
          padding-left: 4px;
        }

        .features-preview li:last-child {
          margin-bottom: 0;
        }

        .login-footer {
          text-align: center;
          color: #6b7280;
        }

        .login-footer p {
          margin: 0;
          font-size: 0.875rem;
        }

        /* Mobile adjustments */
        @media (max-width: 480px) {
          .login-page {
            padding: 16px;
          }

          .login-container {
            gap: 24px;
          }

          /* 🎯 MOBILE LOGO SIZE - ADJUST HERE */
          .logo-image {
            width: 160px;                /* 👈 Smaller size on mobile */
          }

          .login-card {
            padding: 24px;
          }

          .card-header h2 {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;